import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Wordmark } from "./Logo";
import { formatApiErrorDetail } from "../../lib/api";
import { HERO_BG } from "../../data/content";
import { LOGIN, REGISTER } from "../../constants/testIds";
import { Input } from "../ui/input";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success("Welcome to Conexa Relay.");
      nav("/track");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/track";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isLogin = mode === "login";
  const T = isLogin ? LOGIN : REGISTER;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={HERO_BG} alt="Freight highway at night" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 light-streaks opacity-40" />
        <Link to="/" data-testid="login-logo" className="relative">
          <Wordmark variant="dark" sizeClass="h-8" />
        </Link>
        <div className="relative">
          <h2 className="font-heading font-extrabold text-4xl tracking-tighter text-white leading-none">Track every mile.<br /><span className="text-primary">In real time.</span></h2>
          <p className="mt-4 text-white/60 max-w-sm">Sign in to view live shipment status, weather-aware ETAs and your cross-border freight timeline.</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-block mb-8">
            <Wordmark sizeClass="h-8" />
          </Link>
          <span className="font-mono-x text-primary text-xs tracking-[0.35em]">CLIENT PORTAL</span>
          <h1 className="mt-4 font-heading font-extrabold text-4xl tracking-tighter">{isLogin ? "Welcome back" : "Create account"}</h1>
          <p className="mt-2 text-muted-foreground">{isLogin ? "Sign in to track your freight." : "Register to start tracking shipments."}</p>

          <button data-testid="google-login-button" onClick={google}
            className="mt-8 w-full flex items-center justify-center gap-3 rounded-full border border-border py-3 font-medium hover:bg-secondary transition-colors">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex-1 h-px bg-border" /> OR <span className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <Input data-testid={REGISTER.nameInput} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-12" required />
            )}
            <Input data-testid={T.emailInput} type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12" required />
            <Input data-testid={T.passwordInput} type="password" placeholder="Password" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-12" required />
            <button type="submit" disabled={loading} data-testid={T.submitButton}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3.5 hover:shadow-[0_0_30px_hsl(var(--primary)/0.4)] transition-shadow disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{isLogin ? "Sign In" : "Create Account"} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {isLogin ? "New to Conexa Relay? " : "Already have an account? "}
            <button data-testid={isLogin ? LOGIN.registerLink : REGISTER.loginLink} onClick={() => setMode(isLogin ? "register" : "login")} className="text-primary font-semibold hover:underline">
              {isLogin ? "Create an account" : "Sign in"}
            </button>
          </p>

          <div className="mt-8 rounded-xl border border-border bg-card/40 p-4 text-xs text-muted-foreground">
            <span className="font-mono-x tracking-widest text-primary">DEMO ACCESS</span>
            <div className="mt-2">Email: <span className="text-foreground">customer@conexarelay.com</span> · Password: <span className="text-foreground">conexa123</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
